import {
  Assignment,
  Chats,
  CheckCircle,
  FiletypeTxt,
  Flags,
  Hourglass,
  Language,
  Reply,
  Tag,
  ThumbUp,
  VisibilityOn,
} from "@livechat/design-system-icons";
import { IconSource } from "@livechat/design-system-react-components";

import { Author, DetailedSource, TicketPriority, TicketStatus } from "interfaces/entities/ticket";
import { IRule } from "store/entities/rules/interfaces";

export interface IRuleTemplate extends Partial<IRule> {
  createdAt: string;
  createdBy: string;
  createdByType: string;
  updatedAt: string;
  updatedBy: string;
  icon: IconSource;
  premadeType: string;
}

export const EMPTY_RULE: Omit<IRuleTemplate, "icon" | "premadeType"> = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  name: "",
  description: "",
  ordering: 200,
  quantifier: "all",
  triggers: [],
  actions: [],
};

export const FEEDBACK_REQUEST_RULE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  name: "Feedback request",
  icon: ThumbUp,
  description: "If ticket status changes to Solved, send feedback request after 1 hour.",
  ordering: 200,
  quantifier: "all",
  premadeType: "FEEDBACK_REQUEST",
  triggers: [
    {
      in: [],
      type: "teamID",
    },
    {
      in: [TicketStatus.Solved],
      type: "status",
    },
    {
      eq: true,
      type: "hasAssignee",
    },
    {
      eq: 0,
      type: "eventCount",
      eventTypeIn: ["rating", "ratingRequestSent"],
    },
    {
      eq: 3600,
      type: "secondsPassedSinceEvent",
      eventTypeIn: ["status"],
    },
  ],
  actions: [
    {
      type: "sendRatingRequest",
      template: {
        text: "How was your support experience? We\u2019ll appreciate your honest feedback!",
      },
    },
  ],
};

export const PENDING_TO_CLOSED_RULE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  name: "Pending to Closed",
  icon: Hourglass,
  description: "If ticket status is Pending, change status to Closed after 3 days of inactivity.",
  ordering: 400,
  quantifier: "all",
  premadeType: "PENDING_TO_CLOSED",
  triggers: [
    {
      in: [],
      type: "teamID",
    },
    {
      in: [TicketStatus.Pending],
      type: "status",
    },
    {
      eq: 259200,
      type: "secondsPassedSinceEvent",
      eventTypeIn: ["message", "followUpSent", "status"],
    },
  ],
  actions: [
    {
      type: "status",
      status: TicketStatus.Closed,
    },
  ],
};

export const FOLLOW_UP_RULE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  name: "Follow-up",
  icon: Reply,
  description: "If ticket is Pending for 3 days, send follow-up.",
  ordering: 300,
  quantifier: "all",
  premadeType: "FOLLOW_UP",
  triggers: [
    {
      in: [],
      type: "teamID",
    },
    {
      in: [TicketStatus.Pending],
      type: "status",
    },
    {
      eq: 0,
      type: "eventCount",
      eventTypeIn: ["followUpSent"],
    },
    {
      eq: 259200,
      type: "secondsPassedSinceEvent",
      eventTypeIn: ["status", "message", "followUpSent"],
    },
  ],
  actions: [
    {
      type: "sendFollowUp",
      template: {
        text: "Hello {{ticket.requesterName}},\n\n We haven't heard back from you for some time. If you need any further help, please follow up on this email.\n\nThank you.",
      },
    },
  ],
};

export const SECOND_FOLLOW_UP_RULE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Reply,
  name: "Second follow-up",
  description: "If the ticket status is Pending for 3 days after follow-up, send second follow-up.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SECOND_FOLLOW_UP",
  triggers: [
    {
      in: [],
      type: "teamID",
    },
    {
      type: "status",
      in: [TicketStatus.Pending],
    },
    {
      eq: 1,
      type: "eventCount",
      eventTypeIn: ["followUpSent"],
    },
    {
      eq: 259200,
      type: "secondsPassedSinceEvent",
      eventTypeIn: [
        "message",
        "attachments",
        "status",
        "subject",
        "teamVisibility",
        "ticketCreation",
        "cc",
        "assignment",
        "requester",
        "rating",
        "ratingRequestSent",
        "followUpSent",
        "spam",
        "priority",
        "tags",
      ],
    },
  ],
  actions: [
    {
      type: "sendFollowUp",
      template: {
        text: "Hello {{ticket.requesterName}},\n\n We know, you are busy. If your case is still open, please let us know replying to this message. In other case we don't want to disturb you again and close your ticket.\n\nThank you.",
      },
    },
  ],
};

export const PRIVATE_NOTE_ON_TEAM_CHANGE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: FiletypeTxt,
  name: "Send private note when team is changed",
  description: "If the team has been changed, send a private note.",
  ordering: 100,
  quantifier: "all",
  triggers: [{ type: "teamChanged" }],
  premadeType: "PRIVATE_NOTE_ON_TEAM_CHANGE",
  actions: [
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "{{ticket.agentName}}, take a look at this case please.",
      },
    },
    { type: "addFollowers", followers: [] },
  ],
};

export const SET_HIGH_PRIORITY_ON_REQUESTER: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Flags,
  name: "Set a High priority based on the requester",
  description: "If the requester's email is one of the defined ones, set the priority to High.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_HIGH_PRIORITY_ON_REQUESTER",
  triggers: [
    { type: "isNew", eq: true },
    { type: "requesterEmail", in: [] },
  ],
  actions: [
    { type: "priority", priority: TicketPriority.High },
    { type: "assign" },
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "",
      },
    },
  ],
};

export const SET_HIGH_PRIORITY_AFTER_INACTIVITY: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Flags,
  name: "Set the priority to High if there's no activity for 3 days",
  description: "If the ticket is Open and there has been no activity for 3 days, set the priority to High.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_HIGH_PRIORITY_AFTER_INACTIVITY",
  triggers: [
    { type: "teamID", in: [] },
    { type: "status", in: [TicketStatus.Open] },
    {
      eq: 259200,
      type: "secondsPassedSinceEvent",
      eventTypeIn: [
        "message",
        "attachments",
        "status",
        "subject",
        "teamVisibility",
        "ticketCreation",
        "cc",
        "assignment",
        "requester",
        "rating",
        "ratingRequestSent",
        "followUpSent",
        "spam",
        "priority",
        "tags",
      ],
    },
  ],
  actions: [
    { type: "priority", priority: TicketPriority.High },
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "",
      },
    },
  ],
};

export const SET_PRIORITY_TO_URGENT: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  name: "Set priority to Urgent",
  icon: Flags,
  description:
    "If the requester's email contains the defined phrases and there has been no activity for 6 hours, set the priority to Urgent.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_URGENT_PRIORITY",
  triggers: [
    { type: "requesterEmail", containsIn: [] },
    { type: "status", in: [TicketStatus.Open] },
    {
      eq: 21600,
      type: "secondsPassedSinceEvent",
      eventTypeIn: [
        "message",
        "attachments",
        "status",
        "subject",
        "teamVisibility",
        "ticketCreation",
        "cc",
        "assignment",
        "requester",
        "rating",
        "ratingRequestSent",
        "followUpSent",
        "spam",
        "priority",
        "tags",
      ],
    },
    { type: "teamID", in: [] },
  ],
  actions: [
    { type: "priority", priority: TicketPriority.Urgent },
    { type: "addFollowers", followers: [] },
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "",
      },
    },
  ],
};

export const TICKET_ESCALATION: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Flags,
  name: "Ticket escalation",
  description: "If the ticket priority has changed to Urgent, add a follower and send a private note.",
  ordering: 100,
  quantifier: "all",
  premadeType: "TICKET_ESCALATION",
  triggers: [
    { type: "status", in: [TicketStatus.Solved, TicketStatus.Closed], invert: true },
    { type: "priorityChanged", newPriorityIn: [TicketPriority.Urgent] },
    { type: "teamID", in: [] },
  ],
  actions: [
    { type: "addFollowers", followers: [] },
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "",
      },
    },
  ],
};

export const SET_ASSIGNMENT_BASED_ON_SUBJECT: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Assignment,
  name: "Set ticket assignment based on a subject",
  description: "If the ticket subject contains any phrase, set the ticket assignment and add the tag.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_ASSIGNMENT_BASED_ON_SUBJECT",
  triggers: [
    { type: "isNew", eq: true },
    { type: "subject", containsInCI: [] },
  ],
  actions: [{ type: "assign" }, { type: "addTags", tagIDs: [] }],
};

export const SET_ASSIGNMENT_BASED_ON_MESSAGE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Assignment,
  name: "Set ticket assignment based on the message",
  description: "If the new message contains any of the phrases, set the ticket assignment.",
  premadeType: "SET_ASSIGNMENT_BASED_ON_MESSAGE",
  ordering: 100,
  quantifier: "all",
  triggers: [
    { type: "newMessage", containsInCI: [] },
    { type: "teamID", in: [] },
  ],
  actions: [{ type: "assign" }],
};

export const SET_ASSIGNMENT_GENERAL_RANDOMLY: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Assignment,
  name: "Set ticket assignment for general team and an agent using the round-robin method",
  description: "Assign the ticket to the general team and the agent, considering their workload equally.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_ASSIGNMENT_RANDOM_AGENT",
  triggers: [
    { type: "isNew", eq: true },
    { type: "teamID", in: [] },
  ],
  actions: [{ type: "assign", roundRobin: true }],
};

export const SET_ASSIGNMENT_REQUESTER: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Assignment,
  name: "Private support - set ticket assignment based on the requester",
  description: "If the requester is one of the defined ones, then set the ticket assignment.",
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_ASSIGNMENT_BASED_ON_REQUESTER",
  triggers: [
    { type: "isNew", eq: true },
    { type: "requesterEmail", in: [] },
  ],
  actions: [{ type: "assign" }],
};

export const ADD_FOLLOWER_ON_TAG: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: VisibilityOn,
  active: true,
  name: "Add a follower based on a tag",
  description: "If a tag has been added, make the agent follow that ticket.",
  premadeType: "ADD_FOLLOWER_BASED_ON_TAG",
  ordering: 100,
  quantifier: "all",
  triggers: [{ type: "tagAdded", in: [] }],
  actions: [{ type: "addFollowers", followers: [] }],
};

export const ADD_FOLLOWER_BASED_ON_ASSIGNMENT_CHANGE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: VisibilityOn,
  active: true,
  name: "Add a follower based on the assigned original agent",
  description: "If the assignment has been changed, make the original agent follow this ticket.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_FOLLOWER_BASED_ON_ASSIGNMENT_CHANGE",
  triggers: [{ type: "agentChanged" }],
  actions: [{ type: "addFollowers", followers: [] }],
};

export const ADD_TAG_BASED_ON_TEXT: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: Tag,
  active: true,
  name: "Add a tag based on the given text",
  description: "If the new message contains any of the given words, add a specific tag.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_TAG_BASED_ON_MESSAGE",
  triggers: [
    { type: "teamID", in: [] },
    { type: "newMessage", containsInCI: [] },
  ],
  actions: [{ type: "addTags", tagIDs: [] }, { type: "assign" }],
};

export const ADD_TAG_ON_LC_INTEGRATION: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: Chats,
  active: true,
  name: "Add a tag based on LiveChat integration",
  description: "If the ticket is created from LiveChat, add a specific tag.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_TAG_BASED_ON_LIVECHAT_INTEGRATION",
  triggers: [
    { type: "isNew", eq: true },
    { type: "detailedSourceType", in: [DetailedSource.Chat] },
  ],
  actions: [{ type: "addTags", tagIDs: [] }, { type: "assign" }],
};

export const ADD_TAG_ON_REQUESTER: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Tag,
  name: "Add a tag based on the requester",
  description: "If the requester's email contains any of the given phrases, add the tag.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_TAG_BASED_ON_REQUESTER",
  triggers: [
    { type: "isNew", eq: true },
    { type: "requesterEmail", containsIn: [] },
  ],
  actions: [{ type: "addTags", tagIDs: [] }, { type: "assign" }],
};

export const ADD_TAG_ON_TEAM: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Tag,
  name: "Add a tag based on the assigned team",
  description: "If the team has been changed, add a tag and send a private note.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_TAG_BASED_ON_TEAM",
  triggers: [{ type: "teamChanged" }],
  actions: [
    { type: "addTags", tagIDs: [] },
    {
      type: "message",
      isPrivate: true,
      template: {
        text: "",
      },
    },
  ],
};

export const ADD_TAG_ON_FORWARDING_ADDRESS: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Tag,
  name: "Add a tag based on the forwarding address",
  description: "If a ticket is created from a given forwarding address, add the tag.",
  ordering: 100,
  quantifier: "all",
  premadeType: "ADD_TAG_BASED_ON_FORWARDING_ADDRESS",
  triggers: [{ type: "sourceMailbox", in: [] }],
  actions: [{ type: "addTags", tagIDs: [] }],
};

export const SET_TO_SOLVED_ON_TEXT: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: CheckCircle,
  active: true,
  name: "Set status to Solved based on the text",
  description: 'If the new message contains one of the phrases "problem solved," "fixed," set the status to Solved.',
  ordering: 100,
  quantifier: "all",
  premadeType: "SET_TO_SOLVED_ON_MESSAGE",
  triggers: [
    { type: "newMessage", containsInCI: ["problem solved", "fixed"] },
    { type: "teamID", in: [] },
  ],
  actions: [
    { type: "status", status: TicketStatus.Solved },
    { type: "addTags", tagIDs: [] },
  ],
};

export const NEW_TICKET_CONFIRMATION_RULE: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  active: true,
  icon: Reply,
  name: "New ticket confirmation",
  description: "If ticket has been created, notify Requester.",
  ordering: 100,
  quantifier: "all",
  premadeType: "NEW_TICKET_CONFIRMATION",
  triggers: [
    {
      in: [],
      type: "teamID",
    },
    {
      eq: true,
      type: "isNew",
    },
    {
      eq: 1,
      type: "eventCount",
      authorType: Author.Customer,
      eventTypeIn: ["message"],
    },
  ],
  actions: [
    {
      type: "message",
      template: {
        text: "Thank you for your email. We'll reply as soon as we can. Your case number is {{ticket.shortID}}.",
      },
    },
  ],
};

export const LANGUAGE_BASED_ASSIGNMENT: IRuleTemplate = {
  createdAt: "2020-05-20T08:13:04.034Z",
  createdBy: "00000000-0000-4000-8000-000000000000",
  createdByType: "system",
  updatedAt: "2020-05-20T08:13:04.034Z",
  updatedBy: "00000000-0000-4000-8000-000000000000",
  type: "tickets",
  icon: Language,
  active: true,
  name: "Set assignment based on language",
  description: "If the ticket language is one of the defined ones, set the ticket assignment.",
  ordering: 100,
  quantifier: "all",
  premadeType: "LANGUAGE_BASED_ASSIGNMENT",
  triggers: [{ type: "language", in: [] }],
  actions: [{ type: "assign" }],
};

export const PREMADE_RULES_COLLECTION = [
  FEEDBACK_REQUEST_RULE,
  PENDING_TO_CLOSED_RULE,
  FOLLOW_UP_RULE,
  NEW_TICKET_CONFIRMATION_RULE,
  SECOND_FOLLOW_UP_RULE,
  PRIVATE_NOTE_ON_TEAM_CHANGE,
  SET_HIGH_PRIORITY_ON_REQUESTER,
  SET_HIGH_PRIORITY_AFTER_INACTIVITY,
  SET_PRIORITY_TO_URGENT,
  TICKET_ESCALATION,
  SET_ASSIGNMENT_BASED_ON_SUBJECT,
  SET_ASSIGNMENT_BASED_ON_MESSAGE,
  SET_ASSIGNMENT_GENERAL_RANDOMLY,
  SET_ASSIGNMENT_REQUESTER,
  ADD_FOLLOWER_ON_TAG,
  ADD_FOLLOWER_BASED_ON_ASSIGNMENT_CHANGE,
  ADD_TAG_BASED_ON_TEXT,
  ADD_TAG_ON_LC_INTEGRATION,
  ADD_TAG_ON_REQUESTER,
  ADD_TAG_ON_TEAM,
  ADD_TAG_ON_FORWARDING_ADDRESS,
  SET_TO_SOLVED_ON_TEXT,
  LANGUAGE_BASED_ASSIGNMENT,
];
